import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import "./IntroStory.css";

const INTRO_SEEN_KEY = "dmyor_intro_seen";

const SLIDES = [
  {
    mood: "you",
    eyebrow: "what is dmyor",
    heading: "A place to put what you actually want to do with your life.",
    body: "Everyone carries something. A business they want to build. A version of themselves they are trying to become. Something they keep saying they will start.\n\ndmyor is where you finally write it down. Not as a reminder. As a record. Of who you were, what you wanted, and the story of how you got there.",
  },
  {
    mood: "before",
    eyebrow: "who is it for",
    heading: "dmyor is for you if...",
    steps: [
      { label: "You want to start something but are not sure where to begin." },
      { label: "You want to document your journey as you make your vision real." },
      { label: "You want a structured way to plan and execute something that matters to you." },
    ],
  },
  {
    mood: "build",
    eyebrow: "how it works",
    steps: [
      {
        num: "01",
        desc: "Write your Vision. Something you want to make real in 1-5 years.",
      },
      {
        num: "02",
        desc: "Create Records inside it. Each record is a real thing you are doing to make the vision happen.",
      },
      {
        num: "03",
        desc: "Dmyor Assist helps you figure out what records to start with and builds action items so you always know what to do next.",
      },
    ],
  },
  {
    mood: "switch",
    eyebrow: "how it works",
    steps: [
      {
        num: "04",
        desc: "Update your records over time. A win, a setback, a lesson. Every update is a chapter in your story.",
      },
      {
        num: "05",
        desc: "Release a record when you achieve what it set out to do. The story stays. Proof that you built what you said you would.",
      },
    ],
  },
  {
    mood: "proof",
    eyebrow: "and finally",
    heading: "Your vision is public. So is everyone else's.",
    body: "Your vision is anonymous while you are building it. No name, no identity attached. Once you release it, it is no longer anonymous. That is the moment you say: this is mine and I made it real.\n\nWe built dmyor because we believe the dreams people carry deserve to be documented. All of them. In one place.",
    final: true,
  },
];

const markSeen = () => {
  try { localStorage.setItem(INTRO_SEEN_KEY, "1"); } catch (_) {}
};

const IntroStory = ({ onClose }) => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(0);
  const [exiting, setExiting] = useState(false);

  const slide = SLIDES[current];
  const overlayMode = typeof onClose === "function";

  const goNext = () => {
    if (current >= SLIDES.length - 1 || exiting) return;
    setExiting(true);
    setTimeout(() => {
      setCurrent((c) => c + 1);
      setExiting(false);
    }, 320);
  };

  const skip = () => { markSeen(); overlayMode ? onClose() : navigate("/home"); };
  const startBuilding = () => { markSeen(); overlayMode ? onClose("/dashboard/planning/new") : navigate("/dashboard/planning/new"); };
  const goHome = () => { markSeen(); overlayMode ? onClose() : navigate("/home"); };

  return (
    <div className={`is-page is-mood-${slide.mood}${overlayMode ? " is-page--overlay" : ""}`}>
      <div className="is-grain" aria-hidden="true" />
      <div className="is-noise-blobs" aria-hidden="true">
        <span className="is-blob is-blob-a" />
        <span className="is-blob is-blob-b" />
      </div>

      <header className="is-top">
        <span className="is-brand">dmyor</span>
        <button className="is-skip" onClick={skip} type="button">
          skip ✕
        </button>
      </header>

      <main className={`is-stage${exiting ? " is-stage-exit" : ""}`} key={current}>
        <span className="is-eyebrow">⌁ {slide.eyebrow}</span>

        {slide.heading && <h2 className="is-heading">{slide.heading}</h2>}

        {slide.steps ? (
          <ul className="is-steps">
            {slide.steps.map((s, i) => (
              <li key={i} className={`is-step${s.label && !s.num ? ' is-step--labeled' : ''}`}>
                {s.num && <span className="is-step-num">{s.num}</span>}
                {s.label && <span className="is-step-label">{s.label}</span>}
                <span className="is-step-desc">{s.desc}</span>
              </li>
            ))}
          </ul>
        ) : slide.body ? (
          slide.body.includes('\n\n') ? (
            slide.body.split('\n\n').map((para, i) => (
              <p key={i} className="is-body">{para}</p>
            ))
          ) : (
            <p className="is-body">{slide.body}</p>
          )
        ) : null}
      </main>

      <footer className="is-bottom">
        <span className="is-counter">
          {String(current + 1).padStart(2, "0")}
          <span className="is-counter-sep"> / </span>
          {String(SLIDES.length).padStart(2, "0")}
        </span>

        {slide.final ? (
          <div className="is-final-actions">
            <button type="button" className="is-cta-ghost" onClick={goHome}>
              see how it works
            </button>
            <button type="button" className="is-cta-primary" onClick={startBuilding}>
              <span>start building</span>
              <span className="is-arrow">→</span>
            </button>
          </div>
        ) : (
          <button type="button" className="is-next" onClick={goNext}>
            <span>next</span>
            <span className="is-arrow">→</span>
          </button>
        )}
      </footer>
    </div>
  );
};

export default IntroStory;
